/*
 * Copyright (c) 2019.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpErrorResponse} from '@angular/common/http';
import {ErrorHandler, Inject, Injectable, Injector, NgZone} from '@angular/core';
import {Router} from '@angular/router';
import {Environment} from 'compob2b-libs';
import {HTTP_STATUS} from '../constants';
import {StoreService} from '../services/store.service';

@Injectable({
  providedIn: 'root'
})

/**
 * Globally handles auth errors
 * and redirects to sign-in page
 * {@link https://angular.io/api/core/ErrorHandler#description}
 */
export class CompoErrorHandler implements ErrorHandler {

  constructor(private readonly ngZone: NgZone,
              private readonly injector: Injector,
              @Inject('environment') private readonly environment: Environment) {
  }

  handleError(error): void {
    // Не засоряем логи (особенно SSR-файлы) ожидаемыми клиентскими HTTP-ошибками 4xx:
    // это в основном мусор от сканеров и битых ссылок (`/product/.env`, `*.css.map`),
    // и он всё равно обрабатывается навигацией ниже. Логируем только реальные проблемы —
    // не-HTTP ошибки и серверные 5xx.
    const isClientHttpError = error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500;
    if (!isClientHttpError) {
      console.error('Global Error: ', error);
    }

    if (!(error instanceof HttpErrorResponse)) {
      return;
    }

    // Авто-навигация (sign-in / 404) ТОЛЬКО для ошибок собственного бэка (serverApi).
    // Ошибки к другим origin'ам — cross-origin пинги вроде SEO `cabinetHost/healthz`,
    // сторонние сервисы — не должны выкидывать из приложения на авторизацию.
    // error.url=null (CORS-блок / network-fail) — тоже не наш бэк → игнорируем.
    if (!this.isOwnApiError(error)) {
      return;
    }

    const router = this.injector.get(Router);
    if (!this.environment.isOpenCabinet && error.status === HTTP_STATUS.UNAUTHORIZED) {
      // 401 = не аутентифицирован (истёк/невалиден токен) → на форму входа.
      this.ngZone.run(() => router.navigate(['/sign-in']));
    } else if (!this.environment.isOpenCabinet && error.status === HTTP_STATUS.FORBIDDEN) {
      // 403 = залогинен, но нет прав на ресурс. НЕ выкидываем на /sign-in (это рвало
      // вход роль-денишкам через refresh-петлю) — поднимаем флаг «нет доступа» для UX.
      this.injector.get(StoreService).forbiddenError.set(true);
    } else if (this.environment.isOpenCabinet && (error.status === HTTP_STATUS.NOT_FOUND)) {
      this.ngZone.run(() => router.navigate(['/404']));
    }
    // Прочие HTTP-ошибки НЕ ре-throw'им: бросок из ErrorHandler.handleError мог
    // ре-инвокнуть сам обработчик (риск цикла) и всё равно никуда не доходил.
  }

  /** Ошибка пришла от собственного бэка (тот же origin, что serverApi)? */
  private isOwnApiError(error: HttpErrorResponse): boolean {
    if (!error.url) {
      return false;
    }
    try {
      return new URL(error.url).origin === this.environment.apiOrigin;
    } catch {
      return false;
    }
  }
}
